import { sum } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useFormatDate } from '@/composables/useFormatDate'
import { useTransactionStore } from '@/stores/transaction'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import {
  TRANSACTION_BOOLEAN_FIELDS,
  TRANSACTION_DATE_FIELDS,
} from '../constants'
import { soldBuildingPart } from '@/services/soldBuildingPart'
import { num, sumArrayAbs } from '../services/utilities'

export function useHelpers() {
  const { t } = useI18n()
  const { formatDate } = useFormatDate()

  const formatTransactionValue = (field, value) => {
    if (TRANSACTION_BOOLEAN_FIELDS.includes(field)) {
      return value ? 'T' : 'F'
    }
    if (value != null) {
      return value
    }
    return ''
  }

  const isFilterDirty = (filter, etalon) => {
    return filter[0] !== etalon[0] || filter[1] !== etalon[1]
  }

  const iconVariant = (transaction, markedIds) => {
    let variant = 'primary'
    if (transaction.ad_link_not_found) {
      variant = 'warn'
    }
    if (transaction.ad_link) {
      variant = 'success'
    }
    if (transaction.not_vabaturu_tehing) {
      variant = 'secondary'
    }
    if (markedIds.includes(transaction.id)) {
      variant = 'danger'
    }
    return variant
  }

  const calculateUnitPrice = (transaction, field) => {
    return transaction.tehingusumma && transaction[field]
      ? Math.round(num(transaction.tehingusumma) / num(transaction[field]))
      : '-'
  }

  const calculatePrice = (transaction, field, n) => {
    return Math.round(
      num(transaction[field]) + (num(transaction[field]) * num(n)) / 100,
    )
  }

  const sumArray = numbers => {
    return sum(numbers.map(num))
  }

  const formatText = str => {
    if (!str) return ''
    str = str.replaceAll(' (sh ilma loomuliku tõmbe lõõrideta)', '')
    str = str.replaceAll(', näiteks vaht, mull, kergkruus, kärg, betoon', '')
    return str
  }

  const formatFloorFromFloors = (floor, floors) => {
    return `${floors || '-'} / ${floor || '-'}`
  }

  // Land evaluations drop the ventilation segment — irrelevant for an unbuilt
  // parcel whose EHR-building fallback would otherwise render "Ventilatsioon:
  // puudub" (RED-267 PO feedback).
  const formatTechnical = (water, canal, vent, { ventilation = true } = {}) => {
    const parts = [
      `${t(`messages.transactions.ehr_fields.ehitise_veevarustuse_liik`)}: ${water || '-'}`,
      `${t(`messages.transactions.ehr_fields.ehitise_kanalisatsiooni_liik`)}: ${canal || '-'}`,
    ]
    if (ventilation) {
      parts.push(`${t(`messages.transactions.ehr_fields.ehitise_ventilatsioon`)}: ${formatText(vent) || '-'}`)
    }
    return parts.join(';\n        ')
  }

  // RED-267: land Tehnovõrgud composite. Both sides of the kohandustabel use
  // the same per-network keys — the subject from detailStore.ehrCommunal, a
  // comparable from transaction.maatukk — so one formatter keeps the composite
  // strings comparable. Filled keys only; null when nothing is filled.
  const LAND_TECHNICAL_KEYS = [
    'elektrisysteemi_liik',
    'veevarustuse_liik',
    'kanalisatsiooni_liik',
    'soojusvarustuse_liik',
    'gaasi_olemasolu',
    'side',
  ]

  const formatLandTechnical = source => {
    if (!source) return null
    const parts = LAND_TECHNICAL_KEYS.filter(key => source[key]).map(
      key => `${t(`messages.detail.ehrCommunal.${key}`)}: ${source[key]}`,
    )
    // One network per line, like the built-property formatTechnical composite —
    // the tehnovorgud kohandustabel cell renders multiline.
    return parts.length ? parts.join(';\n') : null
  }

  const composeData = (transaction, building, { isLand = false } = {}) => {
    // RED-355: the cells show the part the sale sold, the one an inline edit writes to.
    const buildingPart = soldBuildingPart(transaction)

    return {
      id: transaction ? transaction.id : 'master',
      building_id: building ? building.id : null,
      is_comparable: transaction?.is_comparable ?? !!transaction,
      aadress: transaction ? transaction.hoone_aadress : null,
      ad_link: transaction ? transaction.ad_link : null,
      aol_kpv: transaction
        ? formatTransactionValue('aol_kpv', transaction.aol_kpv ?? transaction.kpv)
        : null,
      tehingusumma: transaction ? transaction.tehingusumma : null,
      sisaldab_km: transaction ? transaction.sisaldab_km : null,
      m2hind: transaction ? 0 : null,
      korteri_pind: transaction ? transaction.korteri_pind : null,
      hooneosa_pind: buildingPart ? (buildingPart.effective_area ?? buildingPart.hooneosa_pind) : null,
      // RED-235: building closed net area (suletud netopind). Feeds the dual
      // "SNP / dwelling area" comparison- and selection-table rows.
      ehitise_suletud_netopind: building ? building.ehitise_suletud_netopind : null,
      // RED-235: per-transaction house flag (üksikelamu only), resolved on the
      // backend (EhrBuildingResource → EhrBuilding::isHousePurposeText). Drives
      // the per-cell dual area, so a non-üksikelamu comparable never shows the
      // building-SNP / unit-area pairing.
      is_house_for_area: building ? !!building.is_house_for_area : false,
      vara_pind: transaction ? 0 : null,
      hooneosa_tubade_arv: buildingPart ? buildingPart.hooneosa_tubade_arv : null,
      korruste_arv:
        transaction && buildingPart
          ? formatFloorFromFloors(
              buildingPart.hooneosa_sissepaasu_korrus,
              building.ehitise_max_korruste_arv,
            )
          : null,
      kandekonstruktsioon: building
        ? formatText(building.ehitise_konstruktsioonide_materjal)
        : null,
      ehitise_esmane_kasutus: building ? building.ehitise_esmane_kasutus : null,
      ehitise_seisukord: building ? building.ehitise_seisukord : null,
      hooneosa_seisukord: buildingPart ? buildingPart.hooneosa_seisukord : null,
      hooneosa_valipindade_pind: buildingPart
        ? buildingPart.hooneosa_valipindade_pind
        : null,
      koogimoobel: null,
      kinnistu_parkimine: building ? building.kinnistu_parkimine : null,
      rodu: buildingPart ? buildingPart.rodu : null,
      panipaik: building ? building.panipaik : null,
      // RED-267: a land transaction's Maatükk-tab per-network enrichment wins;
      // built transactions keep the EHR building composite.
      tehnovorgud:
        formatLandTechnical(transaction?.maatukk) ??
        (building
          ? formatTechnical(
              building.ehitise_veevarustuse_liik,
              building.ehitise_kanalisatsiooni_liik,
              building.ehitise_ventilatsioon,
              { ventilation: !isLand },
            )
          : null),
      // EHR often leaves the per-apartment value empty on centralised systems — fall back to the building-wide field.
      hooneosa_soojavarustuse_liik: buildingPart
        ? (buildingPart.hooneosa_soojavarustuse_liik || building?.ehitise_soojusvarustuse_liik || null)
        : null,
      registriosa: transaction ? transaction.registriosa : null,
      muu_info: transaction?.maatukk?.muu ?? (building ? building.notes : null),
      korteri_ruumilahendus: building ? building.korteri_ruumilahendus : null,
      // RED-267 land elements — base values from the Maatükk-tab enrichment.
      juurdepaas: transaction?.maatukk?.juurdepaas ?? null,
      ehitusoigus: transaction?.maatukk?.ehitusoigus ?? null,
      haljastus: transaction?.maatukk?.haljastus ?? null,
      maatuki_pindala: transaction
        ? transaction.pindala_yhik === 'H'
          ? transaction.pindala * 10000
          : transaction.pindala
        : null,
      vordlustehingu_valik: null,
      ajal_kohandus: transaction ? 0 : null,
      ajal_hind: transaction ? 0 : null,
      asukoha_hinnang: null,
      asukoha_vordlus: null,
      asukoha_kohandus: transaction ? 0 : null,
      hooneosa_vordlus: null,
      hooneosa_kohandus: transaction ? 0 : null,
      tubade_vordlus: null,
      tubade_kohandus: transaction ? 0 : null,
      pindala_vordlus: null,
      pindala_kohandus: transaction ? 0 : null,
      korruste_vordlus: null,
      korruste_kohandus: transaction ? 0 : null,
      ehitise_vordlus: null,
      ehitise_kohandus: transaction ? 0 : null,
      kandekonstruktsioon_vordlus: null,
      kandekonstruktsioon_kohandus: transaction ? 0 : null,
      tehnovorgud_vordlus: null,
      tehnovorgud_kohandus: transaction ? 0 : null,
      hooneosa_soojavarustuse_vordlus: null,
      hooneosa_soojavarustuse_kohandus: transaction ? 0 : null,
      koogimoobel_vordlus: null,
      koogimoobel_kohandus: transaction ? 0 : null,
      rodu_vordlus: null,
      rodu_kohandus: transaction ? 0 : null,
      kinnistu_parkimine_vordlus: null,
      kinnistu_parkimine_kohandus: transaction ? 0 : null,
      panipaik_vordlus: null,
      panipaik_kohandus: transaction ? 0 : null,
      maatuki_pindala_vordlus: null,
      maatuki_pindala_kohandus: transaction ? 0 : null,
      muu_info_vordlus: null,
      muu_info_kohandus: transaction ? 0 : null,
      // RED-267 land element pairs. Seeded here even for non-land contexts —
      // an undefined cell would make updateComparisonTransactionField's
      // oldValue === finalValue short-circuit swallow the first edit.
      juurdepaas_vordlus: null,
      juurdepaas_kohandus: transaction ? 0 : null,
      ehitusoigus_vordlus: null,
      ehitusoigus_kohandus: transaction ? 0 : null,
      haljastus_vordlus: null,
      haljastus_kohandus: transaction ? 0 : null,
      maaramata_kohandus: transaction ? 0 : null,
      sum_kohandus: transaction ? 0 : null,
      kohandatud_hind: transaction ? 0 : null,
      kohanduse_abs: transaction ? 0 : null,
      kaalud: 0,
      kaalutud_keskmine: 0,
      turuvaartus: transaction ? null : 0,
    }
  }

  const clearMarkedStore = async (updateDb = true) => {
    const evaluationStore = useEvaluationStore()
    const transactionStore = useTransactionStore()

    const {
      selectedUnitPriceBase,
      selectedPriceBase,
      selectionTableRows,
      masterData,
      comparisonComponentPage,
    } = storeToRefs(transactionStore)

    const { buildingPart } = storeToRefs(evaluationStore)

    // RED-267: a land parcel's unit price is per m² of the plot.
    selectedUnitPriceBase.value = evaluationStore.isLandParcel
      ? 'maatuki_pindala'
      : 'hooneosa_pind'
    selectedPriceBase.value = 'tehingusumma'
    selectionTableRows.value = []
    transactionStore.resetMarkedTransactions(updateDb)
    masterData.value = null
    transactionStore.clearMasterOverrides()
    comparisonComponentPage.value = 0
    transactionStore.initComparisonTableConfig()
    buildingPart.value = undefined

    if (updateDb) {
      evaluationStore.updateMarkedTransactions()
    }
  }

  const isKomLikeTransaction = transaction => {
    return (
      transaction.objekti_liik === 'KOM' || transaction.objekti_liik === 'KHÕI'
    )
  }

  const isKasiLikeTransaction = transaction => {
    return (
      transaction.objekti_liik === 'KASI' || transaction.objekti_liik === 'HÕIG'
    )
  }

  return {
    formatTransactionValue,
    isFilterDirty,
    iconVariant,
    calculateUnitPrice,
    calculatePrice,
    sumArray,
    sumArrayAbs,
    composeData,
    clearMarkedStore,
    isKomLikeTransaction,
    isKasiLikeTransaction,
    formatFloorFromFloors,
    formatTechnical,
    formatLandTechnical,
    formatText,
  }
}
